import React, { useRef, useEffect } from 'react';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from "react-router-dom";
import { openPositions } from "@/data/positions";

const Careers = () => {
  const openRolesRef = useRef<HTMLDivElement>(null);
  const scrollToOpenRoles = () => {
    openRolesRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Careers | Stratodata"
        description="Join the Stratodata team. We're looking for data engineers, analytics consultants and product managers passionate about retail data."
        canonical="/careers"
      />
      <Navigation />
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-background relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center reveal">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Join Stratodata – Unlocking Business Potential Through Data</h1>
            <p className="text-xl md:text-2xl font-medium text-muted-foreground mb-8">Shape the future of business intelligence, automation, and data-driven decision making. Be part of a team that transforms how companies operate and grow.</p>
            <Button size="lg" onClick={scrollToOpenRoles} className="px-8 py-4 text-lg">Explore Open Roles</Button>
          </div>
        </div>
      </section>
      {/* About the Team */}
      <section className="py-16 bg-secondary/10">
        <div className="container-custom max-w-4xl mx-auto reveal">
          <h2 className="text-3xl font-semibold mb-6 text-center">About the Team</h2>
          <p className="text-lg text-muted-foreground mb-6 text-center">At Stratodata, we're a small, ambitious consultancy on a mission to help companies automate, integrate, and make smarter decisions. We believe every business can unlock new potential through data – and we're building the team to make it happen.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            <Card className="bg-white/80 backdrop-blur-md border-primary/10">
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-2">Our Values</h3>
                <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                  <li>Collaboration & shared growth</li>
                  <li>Continuous learning & curiosity</li>
                  <li>Real-world impact</li>
                  <li>Future-focused, but practical</li>
                  <li>Rooted in today's business needs</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur-md border-primary/10">
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-2">How We Work</h3>
                <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                  <li>Remote-friendly, with hubs in Prague & Žilina</li>
                  <li>Flexible hours & modern tools</li>
                  <li>Supportive, close-knit team</li>
                  <li>Direct client contact & real responsibility</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      {/* Why Work With Us */}
      <section className="py-16 bg-background">
        <div className="container-custom max-w-5xl mx-auto reveal">
          <h2 className="text-3xl font-semibold mb-6 text-center">Why Stratodata?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white/80 backdrop-blur-md border-primary/10">
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-2">Business Impact</h3>
                <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                  <li>Work on projects that automate, integrate, and deliver real-time insights</li>
                  <li>Help clients move from data chaos to clarity</li>
                  <li>See your work drive measurable business growth</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur-md border-primary/10">
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-2">Growth & Mentorship</h3>
                <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                  <li>Mentorship from leaders in BI, automation, and data engineering</li>
                  <li>Training, upskilling, and real client exposure</li>
                  <li>Opportunities to grow fast</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur-md border-primary/10">
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-2">Perks & Flexibility</h3>
                <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                  <li>Flexible hours & remote work</li>
                  <li>We ask you to plan your work around your life, not the life around your work</li>
                  <li>Paid training & certifications</li>
                  <li>Modern tech stack</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      {/* Open Positions */}
      <section className="py-16 bg-secondary/10" ref={openRolesRef}>
        <div className="container-custom max-w-5xl mx-auto reveal">
          <h2 className="text-3xl font-semibold mb-6 text-center">Open Positions</h2>
          <div className="grid grid-cols-1 gap-8">
            {openPositions.map((position) => (
              <Card key={position.id} className="bg-white/90 border-primary/10 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle>{position.title}</CardTitle>
                  {(position.location || position.contract) && (
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground pt-1">
                      {position.location && <span>{position.location}</span>}
                      {position.location && position.contract && <span className="px-1">&bull;</span>}
                      {position.contract && <span>{position.contract}</span>}
                    </div>
                  )}
                  <CardDescription className="pt-2">{position.description}</CardDescription>
                </CardHeader>
                <CardFooter className="flex justify-center">
                  <Button asChild>
                    <Link to={`/careers/${position.id}`}>View Details</Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>
      {/* Footer Section for Careers */}
      <section className="py-12 bg-secondary/20">
        <div className="container-custom max-w-3xl mx-auto text-center reveal">
          <h3 className="text-2xl font-semibold mb-4">Didn't find your dream role?</h3>
          <p className="text-muted-foreground mb-4">We're always looking for talented people who want to shape the future of business intelligence and automation. Send your CV and a few words about yourself to <a href="mailto:martin@stratodata.eu" className="text-primary underline">martin@stratodata.eu</a> </p>
          <p className="text-muted-foreground">See our <a href="/blog" className="text-primary underline">blog</a> and <a href="/case-studies" className="text-primary underline">case studies</a> to learn how we unlock business potential through data.</p>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Careers; 